import { CurrentUser, Permission, PermissionEntry } from '../types';
import {
  getWorkflowTypeFromValue,
  PaymentWorkflowType,
} from '../components/container/forms/types/api/graph.types';
import {
  canUserCreateWorkflow,
  isWorkflowPermissionsBypassed,
} from './workflowRegistry';
import { isOvertimeApprovalBypassEnabled } from './overtimeApprovalBypass';

// ============= Types =============

export type InvolvedWorkflowAssignment = {
  workflowId: number;
  workflowType: PaymentWorkflowType | string | number;
};

// TEMPORARY: Overtime Approval backend permissions are not ready yet.
const TEMPORARY_OVERTIME_CREATE_PERMISSION: PermissionEntry = {
  permission: Permission.CreateOvertimeApproval,
  displayName: 'Create Overtime Approval',
  workflowType: 'OvertimeApproval',
  workflowTypeDisplayName: 'Overtime Approval',
};

const CREATE_WORKFLOW_IDENTIFIERS: Record<
  PaymentWorkflowType,
  ReadonlyArray<string | number>
> = {
  [PaymentWorkflowType.Undefined]: [],
  [PaymentWorkflowType.DubaiPaymentApproval]: [
    PaymentWorkflowType.DubaiPaymentApproval,
    'DubaiPaymentApproval',
    Permission.CreateDubaiPaymentApproval,
    101,
  ],
  [PaymentWorkflowType.DubaiTransferRequisition]: [
    PaymentWorkflowType.DubaiTransferRequisition,
    'DubaiTransferRequisition',
    Permission.CreateDubaiTransferRequisition,
    102,
  ],
  [PaymentWorkflowType.DubaiPaymentFromOfficeAcc]: [
    PaymentWorkflowType.DubaiPaymentFromOfficeAcc,
    'DubaiPaymentFromOfficeAcc',
    Permission.CreateDubaiPaymentFromOfficeAccount,
    103,
  ],
  [PaymentWorkflowType.OvertimeApproval]: [
    PaymentWorkflowType.OvertimeApproval,
    'OvertimeApproval',
    Permission.CreateOvertimeApproval,
    104,
  ],
  [PaymentWorkflowType.GiftHospitalityDonationUk]: [
    PaymentWorkflowType.GiftHospitalityDonationUk,
    'GiftHospitalityDonationUk',
    Permission.CreateGiftHospitalityDonationUk,
  ],
  [PaymentWorkflowType.GiftHospitalityDonationDubai]: [
    PaymentWorkflowType.GiftHospitalityDonationDubai,
    'GiftHospitalityDonationDubai',
    Permission.CreateGiftHospitalityDonationDubai,
  ],
  [PaymentWorkflowType.GiftHospitalityDonationDublin]: [
    PaymentWorkflowType.GiftHospitalityDonationDublin,
    'GiftHospitalityDonationDublin',
    Permission.CreateGiftHospitalityDonationDublin,
  ],
// PLOP_INJECT_CREATE_WORKFLOW_IDENTIFIERS
};

const hasCreateIdentifier = (
  canCreateWorkflowTypes: (string | number)[],
  workflowType: PaymentWorkflowType
): boolean => {
  const identifiers = CREATE_WORKFLOW_IDENTIFIERS[workflowType];
  return canCreateWorkflowTypes.some((type) => identifiers.includes(type));
};

const hasOvertimeCreateType = (
  canCreateWorkflowTypes: (string | number)[]
): boolean =>
  hasCreateIdentifier(
    canCreateWorkflowTypes,
    PaymentWorkflowType.OvertimeApproval
  );

/**
 * TEMPORARY: Overtime Approval backend permissions are not ready yet.
 * Injects create access so the UI treats the user as allowed to create and view overtime.
 */
export const grantTemporaryOvertimeApprovalAccess = (
  user: CurrentUser
): CurrentUser => {
  if (!isOvertimeApprovalBypassEnabled) return user;

  const canCreateWorkflowTypes = user.canCreateWorkflowTypes ?? [];
  const permissions = user.permissions ?? [];
  const hasPermissionEntry = permissions.some(
    (entry) => entry.permission === Permission.CreateOvertimeApproval
  );

  return {
    ...user,
    canCreateWorkflowTypes: hasOvertimeCreateType(canCreateWorkflowTypes)
      ? canCreateWorkflowTypes
      : [...canCreateWorkflowTypes, 'OvertimeApproval'],
    permissions: hasPermissionEntry
      ? permissions
      : [...permissions, TEMPORARY_OVERTIME_CREATE_PERMISSION],
  };
};

export const hasPermission = (
  currentUser: CurrentUser | null | undefined,
  permission: Permission
): boolean => {
  if (!currentUser?.permissions) return false;
  return currentUser.permissions.some(
    (entry) => entry.permission === permission
  );
};

const EDIT_ANY_WORKFLOW_PERMISSIONS = new Set<string>([
  Permission.EditAnyWorkflow,
  Permission.EditAnyWorkflows,
]);

export const canEditAnyWorkflow = (
  currentUser: CurrentUser | null | undefined
): boolean => {
  if (!currentUser?.permissions) return false;
  return currentUser.permissions.some((entry) =>
    EDIT_ANY_WORKFLOW_PERMISSIONS.has(entry.permission)
  );
};

export const canEditOwnWorkflows = (
  currentUser: CurrentUser | null | undefined
): boolean => hasPermission(currentUser, Permission.EditOwnWorkflows);

export const canCreateWorkflowType = (
  currentUser: CurrentUser | null | undefined,
  workflowType: PaymentWorkflowType
): boolean => {
  if (!currentUser) return false;

  // Testing: registry `permissions: true` grants full create access.
  if (isWorkflowPermissionsBypassed(workflowType)) return true;

  // TEMPORARY: Overtime Approval backend permissions are not ready yet.
  if (
    isOvertimeApprovalBypassEnabled &&
    workflowType === PaymentWorkflowType.OvertimeApproval
  )
    return true;

  const canCreateWorkflowTypes = currentUser.canCreateWorkflowTypes ?? [];
  if (hasCreateIdentifier(canCreateWorkflowTypes, workflowType)) return true;

  return canUserCreateWorkflow(
    workflowType,
    canCreateWorkflowTypes,
    currentUser.permissions || []
  );
};

export const canViewWorkflow = (
  currentUser: CurrentUser | null | undefined,
  workflowType: PaymentWorkflowType
): boolean => {
  if (!currentUser) return false;

  // Testing: registry `permissions: true` grants full view access.
  if (isWorkflowPermissionsBypassed(workflowType)) return true;

  if (currentUser.canViewAllWorkflows) return true;
  return canCreateWorkflowType(currentUser, workflowType);
};

export type InvolvementIdentity = {
  userEmail?: string | null;
  userDisplayName?: string | null;
  userId?: string | null;
  userUpn?: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeIdentity = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'");
};

const formatCommaSeparatedDisplayName = (displayName: string): string => {
  const [lastName, firstName] = displayName.split(',').map((part) => part.trim());
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  return displayName.trim();
};

const emailFromGuestUpn = (upn: string): string => {
  const extIndex = upn.toLowerCase().indexOf('#ext#');
  if (extIndex <= 0) return '';
  const local = upn.slice(0, extIndex);
  const separator = local.lastIndexOf('_');
  if (separator <= 0 || separator === local.length - 1) return '';
  return `${local.slice(0, separator)}@${local.slice(separator + 1)}`;
};

const readOptionalString = (
  value: Record<string, unknown>,
  key: string
): string => (typeof value[key] === 'string' ? value[key] : '');

/**
 * Spoof APIs expect an email. Guest picker values are often the UPN
 * (`name_domain#EXT#@tenant`), which does not match involved user emails.
 */
export const getSpoofUserHeader = (
  spoofedUser: { value?: string; email?: string } | null | undefined
): string | null => {
  const email = spoofedUser?.email?.trim();
  if (email) return email;

  const value = spoofedUser?.value?.trim();
  if (!value) return null;

  return emailFromGuestUpn(value) || value;
};

export const identityFromSpoofedUser = (
  spoofedUser: { label?: string; value?: string; email?: string } | null | undefined
): InvolvementIdentity | null => {
  if (!spoofedUser) return null;
  return {
    userEmail: spoofedUser.email || getSpoofUserHeader(spoofedUser),
    userDisplayName: spoofedUser.label,
    userUpn: spoofedUser.value,
  };
};

const getUserIdentityValues = (
  user: CurrentUser | InvolvementIdentity | null | undefined
): string[] => {
  if (!user) return [];

  const record = user as Record<string, unknown>;
  const displayName =
    ('userDisplayName' in user ? user.userDisplayName : undefined) ?? '';
  const userUpn = readOptionalString(record, 'userUpn');

  return [
    'userEmail' in user ? user.userEmail : undefined,
    displayName,
    formatCommaSeparatedDisplayName(displayName),
    'userId' in user ? user.userId : undefined,
    userUpn,
    emailFromGuestUpn(userUpn),
  ]
    .map(normalizeIdentity)
    .filter(Boolean);
};

/**
 * True when the user has an active assignment on this specific workflow.
 */
export const isInvolvedWithWorkflow = (
  assignedWorkflows: ReadonlyArray<InvolvedWorkflowAssignment> | null | undefined,
  workflowId: number | string | null | undefined
): boolean => {
  if (!assignedWorkflows?.length || workflowId == null || workflowId === '') {
    return false;
  }

  const id = Number(workflowId);
  if (!Number.isFinite(id)) return false;

  return assignedWorkflows.some((assignment) => assignment.workflowId === id);
};

/**
 * True when the user has an active assignment on any workflow of this type.
 */
export const isInvolvedWithWorkflowType = (
  assignedWorkflows: ReadonlyArray<InvolvedWorkflowAssignment> | null | undefined,
  workflowType: PaymentWorkflowType
): boolean => {
  if (!assignedWorkflows?.length) return false;
  if (workflowType === PaymentWorkflowType.Undefined) return false;

  return assignedWorkflows.some(
    (assignment) =>
      getWorkflowTypeFromValue(assignment.workflowType) === workflowType
  );
};

/**
 * True when an involvement-permissions payload shows the current user is involved
 * in that workflow (assigned, creator, or flagged involved).
 */
export const isInvolvedFromPermissions = (
  involvementData: unknown,
  ...users: Array<CurrentUser | InvolvementIdentity | null | undefined>
): boolean => {
  if (!isRecord(involvementData)) return false;

  if (involvementData.userIsInvolved === true) return true;

  if (
    Array.isArray(involvementData.userAssignedSections) &&
    involvementData.userAssignedSections.length > 0
  ) {
    return true;
  }

  if (!Array.isArray(involvementData.involvedUsers)) {
    return false;
  }

  const identities = new Set(
    users.flatMap((user) => getUserIdentityValues(user))
  );
  if (identities.size === 0) return false;

  return involvementData.involvedUsers.some((involvedUser) => {
    if (!isRecord(involvedUser)) return false;

    const displayName = readOptionalString(involvedUser, 'displayName');
    const involvedIdentities = [
      involvedUser.email,
      involvedUser.displayName,
      formatCommaSeparatedDisplayName(displayName),
      involvedUser.userId,
      involvedUser.userUpn,
    ]
      .map(normalizeIdentity)
      .filter(Boolean);

    return involvedIdentities.some((identity) => identities.has(identity));
  });
};

/**
 * Show workflows of this type when the user has type permission or is involved
 * with at least one workflow of that type.
 */
export const canAccessWorkflowType = (
  currentUser: CurrentUser | null | undefined,
  workflowType: PaymentWorkflowType,
  assignedWorkflows?: ReadonlyArray<InvolvedWorkflowAssignment> | null
): boolean => {
  // Testing: registry `permissions: true` grants dashboard/list access.
  if (isWorkflowPermissionsBypassed(workflowType)) return true;

  if (canViewWorkflow(currentUser, workflowType)) return true;
  return isInvolvedWithWorkflowType(assignedWorkflows, workflowType);
};

/**
 * Show a specific workflow when the user has type permission or is involved
 * with that workflow instance.
 */
export const canAccessWorkflow = (
  currentUser: CurrentUser | null | undefined,
  workflowType: PaymentWorkflowType,
  involvementData?: unknown,
  assignedWorkflows?: ReadonlyArray<InvolvedWorkflowAssignment> | null,
  workflowId?: number | string | null,
  spoofedIdentity?: InvolvementIdentity | null
): boolean => {
  // Testing: registry `permissions: true` grants instance access.
  if (isWorkflowPermissionsBypassed(workflowType)) return true;

  if (canViewWorkflow(currentUser, workflowType)) return true;
  if (
    isInvolvedFromPermissions(involvementData, currentUser, spoofedIdentity)
  ) {
    return true;
  }
  return isInvolvedWithWorkflow(assignedWorkflows, workflowId);
};

export const canSpoof = (
  currentUser: CurrentUser | null | undefined
): boolean => {
  return hasPermission(currentUser, Permission.Spoof);
};

export const canReassign = (
  currentUser: CurrentUser | null | undefined
): boolean => {
  return hasPermission(currentUser, Permission.Reassign);
};

/**
 * Checks if the current user or spoofed user matches any name in the responsible_lawyer array
 * @param responsibleLawyer Array of strings or objects with value property representing lawyer names
 * @param currentUser Current user object with userDisplayName
 * @param spoofedUser Email string like "D.Miller@taylorwessing.com" or null
 * @returns true if user matches any responsible lawyer name
 */
export const isUserResponsibleLawyer = (
  responsibleLawyer: unknown[] | undefined | null,
  currentUser: CurrentUser | null | undefined,
  spoofedUser: string | null | undefined
): boolean => {
  if (
    !responsibleLawyer ||
    !Array.isArray(responsibleLawyer) ||
    responsibleLawyer.length === 0
  ) {
    return false;
  }

  // Extract names from responsible_lawyer array
  // Handle both string arrays ["Natasha Zahid"] and object arrays [{ value: "Natasha Zahid" }]
  const lawyerNames = responsibleLawyer
    .map((item) => {
      if (typeof item === 'string') {
        return item.trim();
      }
      if (item && typeof item === 'object' && 'value' in item) {
        return typeof item.value === 'string' ? item.value.trim() : '';
      }
      return '';
    })
    .filter((name) => name.length > 0);

  if (lawyerNames.length === 0) {
    return false;
  }

  // Normalize names for comparison (case-insensitive)
  const normalizeName = (name: string): string => {
    return name.toLowerCase().trim();
  };

  // Check if current user matches
  if (currentUser?.userDisplayName) {
    // Convert "Miller, Dawn" format to "Dawn Miller"
    const [lastName, firstName] = currentUser.userDisplayName
      .split(',')
      .map((s) => s.trim());
    if (firstName && lastName) {
      const formattedName = `${firstName} ${lastName}`;
      const normalizedDisplayName = normalizeName(formattedName);

      // Check exact match
      if (
        lawyerNames.some(
          (name) => normalizeName(name) === normalizedDisplayName
        )
      ) {
        return true;
      }
    }
  }

  // Check if spoofed user matches
  if (spoofedUser) {
    // Extract name from email like "D.Miller@taylorwessing.com" -> "D.Miller"
    const emailName = spoofedUser.split('@')[0]?.trim();
    if (emailName) {
      // Split by dot to get first initial and last name: "D.Miller" -> ["D", "Miller"]
      const parts = emailName.split('.');
      if (parts.length >= 2) {
        const firstInitial = parts[0]?.trim().toUpperCase();
        const lastName = parts[parts.length - 1]?.trim();

        if (firstInitial && lastName) {
          // Check if any lawyer name matches the pattern
          // Match formats like "D. Miller", "D Miller", "Dawn Miller" (where first name starts with D)
          for (const lawyerName of lawyerNames) {
            const normalized = normalizeName(lawyerName);
            const normalizedLastName = normalizeName(lastName);

            // Check exact formats first: "D. Miller" or "D Miller"
            if (
              normalized === normalizeName(`${firstInitial}. ${lastName}`) ||
              normalized === normalizeName(`${firstInitial} ${lastName}`)
            ) {
              return true;
            }

            // Check if name ends with last name
            if (normalized.endsWith(normalizedLastName)) {
              // Extract everything before the last name
              const beforeLastName = normalized
                .slice(0, -normalizedLastName.length)
                .trim();

              if (beforeLastName) {
                // There's a first name, check if it starts with the initial
                const firstPart = beforeLastName.split(/\s+/)[0];
                if (
                  firstPart &&
                  firstPart.charAt(0) === firstInitial.toLowerCase()
                ) {
                  return true;
                }
              } else if (normalized === normalizedLastName) {
                // Just last name match (could be a match)
                return true;
              }
            }
          }
        }
      }
    }
  }

  return false;
};

/**
 * True when the current user or spoofed user is the overtime line manager.
 * `lineManager` may be an email string, a tag-picker option, or an array of those.
 */
export const isManager = (
  lineManager: unknown,
  currentUser: CurrentUser | null | undefined,
  spoofedUser: string | null | undefined
): boolean => {
  const managerIdentities = extractManagerIdentities(lineManager);
  if (managerIdentities.length === 0) return false;

  const identities = new Set([
    ...getUserIdentityValues(currentUser),
    ...getUserIdentityValues({
      userEmail: spoofedUser,
      userUpn: spoofedUser,
    }),
  ]);

  if (identities.size === 0) return false;

  return managerIdentities.some((identity) => identities.has(identity));
};

const extractManagerIdentities = (lineManager: unknown): string[] => {
  if (!lineManager) return [];

  if (typeof lineManager === 'string') {
    const normalized = normalizeIdentity(lineManager);
    return normalized ? [normalized] : [];
  }

  if (Array.isArray(lineManager)) {
    return lineManager.flatMap((item) => extractManagerIdentities(item));
  }

  if (!isRecord(lineManager)) return [];

  return [
    lineManager.value,
    lineManager.email,
    lineManager.label,
    lineManager.mail,
    lineManager.userEmail,
    lineManager.userDisplayName,
  ]
    .map(normalizeIdentity)
    .filter(Boolean);
};
